/**
 * Unified data parser for SplitModelSetSlideTable component
 * Handles conversion from different API response structures to unified format
 */

import { UnifiedModelSetData, DataSourceType, ParserResult } from '../types';

/**
 * Main parser function that automatically detects data type and converts to unified format
 */
export function parseModelSetData(
  data: unknown,
  dataType: DataSourceType = 'auto',
): ParserResult {
  try {
    // Auto-detect data type if not specified
    if (dataType === 'auto') {
      dataType = detectDataType(data);
    }

    let unifiedData: UnifiedModelSetData;

    switch (dataType) {
      case 'premium-report':
        unifiedData = parsePremiumReportData(data);
        break;
      case 'performance-report':
        unifiedData = parsePerformanceReportData(data);
        break;
      case 'cases':
        unifiedData = parseCasesData(data);
        break;
      default:
        throw new Error(`Unsupported data type: ${dataType}`);
    }

    return {
      success: true,
      data: unifiedData,
      detectedType: dataType,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown parsing error',
    };
  }
}

/**
 * Auto-detect data type based on structure
 */
function detectDataType(data: unknown): DataSourceType {
  const dataObj = data as any;

  // Check for Cases data structure
  if (
    dataObj?.value?.model_set_holdings &&
    Array.isArray(dataObj.value.model_set_holdings)
  ) {
    return 'cases';
  }

  // Check for Premium Report data structure
  if (
    dataObj?.data?.generatedCommentaries?.model_set_holdings &&
    Array.isArray(dataObj.data.generatedCommentaries.model_set_holdings)
  ) {
    return 'premium-report';
  }

  // Check for Performance Report data structure
  if (
    dataObj?.portfolio_options &&
    Array.isArray(dataObj.portfolio_options) &&
    dataObj?.proxy_metadata
  ) {
    return 'performance-report';
  }

  // Check for direct model set data (fallback)
  if (dataObj?.portfolio_options && Array.isArray(dataObj.portfolio_options)) {
    return 'premium-report'; // Default to premium report structure
  }

  throw new Error(
    'Unable to detect data type. Please specify dataType explicitly.',
  );
}

/**
 * Parse Premium Report API response
 */
function parsePremiumReportData(data: unknown): UnifiedModelSetData {
  const dataObj = data as any;
  const modelSetHoldings =
    dataObj.data?.generatedCommentaries?.model_set_holdings ||
    dataObj.model_set_holdings;

  if (!modelSetHoldings || !Array.isArray(modelSetHoldings)) {
    throw new Error('Invalid Premium Report data structure');
  }

  // For now, take the first model set (can be extended to handle multiple)
  const modelSet = modelSetHoldings[0];

  return {
    model_set_name: modelSet.model_set_name,
    abbreviated_model_set_name: modelSet.abbreviated_model_set_name,
    metadata: {
      objective: modelSet.metadata?.objective,
      asset_size: modelSet.metadata?.asset_size,
      taxable_status: modelSet.metadata?.taxable_status,
      model_explaination: modelSet.model_explaination,
    },
    portfolio_options: modelSet.portfolio_options.map((option: any) => ({
      option: option.option,
      is_final: option.is_final,
      portfolios: option.portfolios.map((portfolio: any) => ({
        risk_profile: portfolio.risk_profile,
        holdings: portfolio.holdings.map((holding: any) => ({
          strategy: holding.strategy,
          profile: holding.profile,
          category_role: holding.category_role,
          weight: holding.weight,
          model_id: holding.model_id,
          description: holding.description,
          full_model_name: holding.full_model_name,
          implementation: holding.implementation,
          investment_minimum: holding.investment_minimum,
          main_purpose: holding.main_purpose,
          platform_fee: holding.platform_fee,
          role: holding.role,
          short_description: holding.short_description,
          strategist: holding.strategist,
          inception_date: holding.inception_date,
          category: holding.category,
        })),
      })),
    })),
  };
}

/**
 * Parse Performance Report API response
 */
function parsePerformanceReportData(data: unknown): UnifiedModelSetData {
  const dataObj = data as any;
  if (!dataObj.portfolio_options || !Array.isArray(dataObj.portfolio_options)) {
    throw new Error('Invalid Performance Report data structure');
  }

  return {
    model_set_name: dataObj.model_set_name,
    abbreviated_model_set_name: dataObj.abbreviated_model_set_name,
    portfolio_options: dataObj.portfolio_options.map((option: any) => ({
      option: option.option,
      portfolios: option.portfolios.map((portfolio: any) => ({
        risk_profile: portfolio.profile, // Note: Performance report uses 'profile' instead of 'risk_profile'
        portfolio_name: portfolio.portfolio_name,
        display_portfolio_name: portfolio.display_portfolio_name,
        benchmark_name: portfolio.benchmark_name,
        display_benchmark_name: portfolio.display_benchmark_name,
        portfolio_statistics: portfolio.portfolio_statistics,
        benchmark_statistics: portfolio.benchmark_statistics,
        // Performance reports don't have holdings, so we create empty holdings array
        holdings: [],
      })),
    })),
  };
}

/**
 * Parse Cases API response (CaseMilestoneAction)
 */
function parseCasesData(data: unknown): UnifiedModelSetData {
  const dataObj = data as any;
  const modelSetHoldings = dataObj.value?.model_set_holdings;

  if (!modelSetHoldings || !Array.isArray(modelSetHoldings)) {
    throw new Error('Invalid Cases data structure');
  }

  // For now, take the first model set (can be extended to handle multiple)
  const modelSet = modelSetHoldings[0];

  return {
    model_set_name: modelSet.model_set_name,
    abbreviated_model_set_name: modelSet.abbreviated_model_set_name,
    portfolio_options: modelSet.portfolio_options.map((option: any) => ({
      option: option.option,
      is_final: option.is_final,
      portfolios: option.portfolios.map((portfolio: any) => ({
        risk_profile: portfolio.risk_profile,
        holdings: portfolio.holdings.map((holding: any) => ({
          strategy: holding.strategy,
          profile: holding.profile,
          category_role: holding.category_role,
          weight: holding.weight,
          model_id: holding.model_id,
          description: holding.description,
          full_model_name: holding.full_model_name,
          implementation: holding.implementation,
          investment_minimum: holding.investment_minimum,
          main_purpose: holding.main_purpose,
          platform_fee: holding.platform_fee,
          role: holding.role,
          short_description: holding.short_description,
          strategist: holding.strategist,
          inception_date: holding.inception_date,
          category: holding.category,
        })),
      })),
    })),
  };
}

/**
 * Validate unified data structure
 */
export function validateUnifiedData(data: UnifiedModelSetData): boolean {
  if (
    !data.model_set_name ||
    !data.portfolio_options ||
    !Array.isArray(data.portfolio_options)
  ) {
    return false;
  }

  return data.portfolio_options.every(
    option =>
      typeof option.option === 'number' &&
      Array.isArray(option.portfolios) &&
      option.portfolios.every(
        portfolio =>
          typeof portfolio.risk_profile === 'number' &&
          Array.isArray(portfolio.holdings),
      ),
  );
}

/**
 * Get data source type from unified data
 */
export function getDataSourceType(data: UnifiedModelSetData): DataSourceType {
  // Check if data has metadata (Premium Report)
  if (data.metadata) {
    return 'premium-report';
  }

  // Check if portfolios have statistics (Performance Report)
  if (
    data.portfolio_options.some(option =>
      option.portfolios.some(
        portfolio =>
          portfolio.portfolio_statistics || portfolio.benchmark_statistics,
      ),
    )
  ) {
    return 'performance-report';
  }

  // Default to cases for now
  return 'cases';
}
